import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, Clock, ListChecks, Users as UsersIcon, Flame } from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const STATUS_COLORS = {
  Pending: "#f59e0b",
  "In-Progress": "#6366f1",
  Done: "#10b981",
};

function KpiCard({ label, value, icon: Icon, accent, testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-md p-5 card-lift" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <div className="label-eyebrow">{label}</div>
          <div className="mt-3 text-3xl font-black tracking-tight text-slate-900">{value}</div>
        </div>
        <div className={`h-9 w-9 rounded-md flex items-center justify-center ${accent}`}>
          <Icon className="h-4 w-4" />
        </div>
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/dashboard/summary");
        setSummary(data);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const statusData = useMemo(() => {
    if (!summary) return [];
    return [
      { name: "Pending", key: "Pending", value: summary.pending || 0 },
      { name: "In Progress", key: "In-Progress", value: summary.in_progress || 0 },
      { name: "Done", key: "Done", value: summary.done || 0 },
    ];
  }, [summary]);

  const teamData = useMemo(() => {
    if (!summary?.team) return [];
    return summary.team
      .slice()
      .sort((a, b) => b.total - a.total)
      .slice(0, 8)
      .map((e) => ({
        name: e.name?.split(" ")[0] || "—",
        Done: e.done || 0,
        Pending: e.pending || 0,
        InProgress: e.in_progress || 0,
      }));
  }, [summary]);

  if (loading) return <div className="label-eyebrow">Loading…</div>;
  if (!summary) return null;

  const overdue = summary.overdue ?? 0;

  return (
    <div className="space-y-8">
      <div>
        <div className="label-eyebrow">Overview</div>
        <h1 className="mt-1 text-3xl sm:text-4xl font-black tracking-tight text-slate-900">
          Good day, {user.name.split(" ")[0]}.
        </h1>
        <p className="mt-2 text-sm text-slate-500">
          {user.role === "Employee"
            ? "Here's a snapshot of tasks assigned to you."
            : user.role === "Manager"
            ? "Here's how your team is tracking today."
            : "Full dealership operations at a glance."}
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="kpi-grid">
        <KpiCard label="Total tasks" value={summary.total} icon={ListChecks} accent="bg-slate-100 text-slate-700" testid="kpi-total" />
        <KpiCard label="Pending" value={summary.pending} icon={Clock} accent="bg-amber-100 text-amber-700" testid="kpi-pending" />
        <KpiCard label="In progress" value={summary.in_progress} icon={Clock} accent="bg-indigo-100 text-indigo-700" testid="kpi-in-progress" />
        <KpiCard label="Completed" value={summary.done} icon={CheckCircle2} accent="bg-emerald-100 text-emerald-700" testid="kpi-done" />
        <KpiCard label={overdue > 0 ? "Overdue" : "High priority"} value={overdue > 0 ? overdue : (summary.high_priority_open || 0)} icon={overdue > 0 ? AlertTriangle : Flame} accent={overdue > 0 ? "bg-rose-100 text-rose-700" : "bg-orange-100 text-orange-700"} testid="kpi-overdue" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-1 bg-white border border-slate-200 rounded-md p-5">
          <div className="label-eyebrow">Status breakdown</div>
          <div className="mt-4" style={{ width: "100%", minHeight: 256, height: 256 }}>
            <ResponsiveContainer width="100%" height={256}>
              <PieChart>
                <Pie
                  data={statusData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  paddingAngle={2}
                >
                  {statusData.map((entry) => (
                    <Cell key={entry.key} fill={STATUS_COLORS[entry.key]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-md p-5">
          <div className="flex items-center gap-2">
            <UsersIcon className="h-4 w-4 text-slate-500" />
            <div className="label-eyebrow">Team performance</div>
          </div>
          <div className="mt-4" style={{ width: "100%", minHeight: 256, height: 256 }}>
            {teamData.length === 0 ? (
              <div className="h-full flex items-center justify-center text-sm text-slate-500">
                No task data yet.
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={256}>
                <BarChart data={teamData} margin={{ top: 5, right: 8, left: -20, bottom: 5 }}>
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="Done" stackId="a" fill="#10b981" />
                  <Bar dataKey="InProgress" stackId="a" fill="#6366f1" />
                  <Bar dataKey="Pending" stackId="a" fill="#f59e0b" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
